import { camelcaseKeysFromApi, snakecaseKeysForApi } from '@vestigas/data';

import { type DataSyncJob, type DataSyncTask } from './validations/types';

/**
 * Data sync task fields whose object keys are defined by the customer's system
 * instead of by us, listed as [API name, UI name]. The API client's case
 * conversion rewrites such keys (`Client-Context` -> `client_context`) and so
 * breaks the sync, which is why these are passed through verbatim.
 */
const USER_KEYED_FIELDS = [
  ['api_body', 'apiBody'],
  ['api_headers', 'apiHeaders'],
  ['api_params', 'apiParams'],
  ['auth_api_body', 'authApiBody'],
  ['auth_api_headers', 'authApiHeaders'],
  ['auth_api_params', 'authApiParams'],
  ['auth_custom_headers', 'authCustomHeaders'],
  ['auth_steps', 'authSteps'],
  ['value_mapping', 'valueMapping'],
] as const;

/**
 * Splits a raw record into the part that is safe to case-convert and the
 * user-keyed fields, the latter renamed per the given [API name, UI name]
 * pairs. A null the API sent is kept, so the form shows the field as stored.
 */
function splitUserKeyedFields(
  record: Record<string, unknown>,
  fields: ReadonlyArray<readonly [string, string]>,
) {
  const convertible = { ...record };
  const preserved: Record<string, unknown> = {};

  for (const [apiName, uiName] of fields) {
    if (convertible[apiName] !== undefined) {
      preserved[uiName] = convertible[apiName];
    }
    delete convertible[apiName];
  }

  return { convertible, preserved };
}

/**
 * Converts an API response to the UI format, keeping user-defined keys intact.
 */
export function fromApiKeepingUserKeys(
  response: Record<string, unknown>,
): DataSyncTask {
  const { convertible, preserved } = splitUserKeyedFields(
    response,
    USER_KEYED_FIELDS,
  );

  return {
    ...camelcaseKeysFromApi(convertible),
    ...preserved,
  } as DataSyncTask;
}

/**
 * Data sync job fields whose object keys come from a template rather than from
 * this API, listed as [API name, UI name]. Same reason as the task fields
 * above: `template_params` is keyed by the template's own field names
 * (`base_url`), and case conversion would rewrite them so the setup form could
 * no longer look its own values up.
 */
const JOB_USER_KEYED_FIELDS = [['template_params', 'templateParams']] as const;

/**
 * Converts a job response, keeping user-defined keys intact in its nested tasks
 * and in the template parameters it was created from.
 */
export function jobFromApiKeepingUserKeys(
  rawJob: Record<string, unknown>,
): DataSyncJob {
  const { data_sync_tasks, ...rest } = rawJob;
  // Anything but a list is not a task list: a truthiness check would hand a
  // string or an object straight to `.map`.
  const tasks = Array.isArray(data_sync_tasks)
    ? (data_sync_tasks as Record<string, unknown>[]).map(fromApiKeepingUserKeys)
    : [];

  const { convertible, preserved } = splitUserKeyedFields(
    rest,
    JOB_USER_KEYED_FIELDS,
  );

  return {
    ...camelcaseKeysFromApi(convertible),
    ...preserved,
    dataSyncTasks: tasks,
  } as DataSyncJob;
}

/**
 * Converts validated form data to an API payload, keeping user-defined keys
 * intact.
 *
 * A field that is null or missing is left out rather than sent as null: the
 * update is applied with exclude_unset, so leaving it out means "unchanged".
 */
export function toApiKeepingUserKeys(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const convertible = { ...data };
  const preserved: Record<string, unknown> = {};

  for (const [apiName, uiName] of USER_KEYED_FIELDS) {
    if (convertible[uiName] != null) {
      preserved[apiName] = convertible[uiName];
    }
    delete convertible[uiName];
  }

  return {
    ...(snakecaseKeysForApi(convertible) as Record<string, unknown>),
    ...preserved,
  };
}
